import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { MediaItem } from "@/types/media";
import { typeLabelMap } from "@/utils/media";

interface HeroCarouselProps {
  items: MediaItem[];
}

export default function HeroCarousel({ items }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused || items.length <= 1) return;
    const timer = window.setInterval(() => {
      setIndex((current) => (current + 1) % items.length);
    }, 6500);
    return () => window.clearInterval(timer);
  }, [paused, items.length]);

  if (items.length === 0) return null;

  const active = items[index];
  const goPrev = () => setIndex((current) => (current - 1 + items.length) % items.length);
  const goNext = () => setIndex((current) => (current + 1) % items.length);

  return (
    <section
      className="relative w-full overflow-hidden rounded-xl bg-primary-950"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      aria-label="焦点影视推荐"
    >
      <div className="relative h-[470px] w-full sm:h-[520px] lg:h-[580px]">
        {items.map((item, itemIndex) => (
          <div
            key={item.id}
            className={`absolute inset-0 transition-opacity duration-700 ${
              itemIndex === index ? "opacity-100" : "pointer-events-none opacity-0"
            }`}
          >
            <img
              src={item.backdrop || item.cover}
              alt={`${item.title} 剧照`}
              title={`${item.title} - 日韩影视推荐`}
              className={`h-full w-full object-cover object-top ${itemIndex === index ? "rk-ken-burns" : ""}`}
            />
          </div>
        ))}

        <div className="absolute inset-0 rk-hero-mask" />
        <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-primary-950 via-primary-950/60 to-transparent" />

        <div className="relative z-10 flex h-full flex-col justify-end px-5 pb-8 sm:px-8 md:px-10 lg:px-14 lg:pb-14">
          <div key={active.id} className="rk-fade-up max-w-[620px]">
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-500 px-3 py-1 text-[11px] font-semibold text-primary-950">
                <span className="h-1.5 w-1.5 rounded-full bg-primary-950" />
                本周焦点
              </span>
              <span className="rounded-full border border-background-50/30 px-2.5 py-1 text-[11px] text-background-100">
                {typeLabelMap[active.type]} · {active.region}
              </span>
              <span className="rounded-full border border-background-50/30 px-2.5 py-1 text-[11px] text-background-100">
                {active.status}
              </span>
            </div>

            <h2 className="mt-4 font-heading text-3xl font-semibold leading-tight text-background-50 sm:text-4xl lg:text-[52px]">
              {active.title}
            </h2>
            <p className="mt-1.5 font-label text-xs uppercase tracking-[0.18em] text-background-300">
              {active.originalTitle}
            </p>

            <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-background-100">
              <span className="flex items-center gap-1 font-semibold text-accent-300">
                <i className="ri-star-fill text-sm" />
                {active.rating.toFixed(1)}
              </span>
              <span>{active.year}</span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line text-sm text-background-300" />
                {active.episodes}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-user-star-line text-sm text-background-300" />
                {active.director}
              </span>
            </div>

            <p className="mt-3 line-clamp-2 max-w-[540px] text-sm leading-relaxed text-background-200 sm:line-clamp-3">
              {active.synopsis}
            </p>

            <div className="mt-6 flex flex-wrap items-center gap-3">
              <Link
                to={`/detail/${active.id}`}
                className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-semibold text-primary-950 transition-colors hover:bg-accent-400"
              >
                <i className="ri-play-fill text-base" />
                查看详情
              </Link>
              <Link
                to={`/category/${active.type}`}
                className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-50/35 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:border-background-50/70 hover:bg-background-50/10"
              >
                更多{typeLabelMap[active.type]}
                <i className="ri-arrow-right-line text-sm" />
              </Link>
            </div>
          </div>

          <div className="mt-6 flex items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              {items.map((item, itemIndex) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setIndex(itemIndex)}
                  aria-label={`切换到 ${item.title}`}
                  aria-current={itemIndex === index}
                  className={`h-1 cursor-pointer rounded-full transition-all duration-300 ${
                    itemIndex === index ? "w-8 bg-accent-500" : "w-4 bg-background-50/40 hover:bg-background-50/70"
                  }`}
                />
              ))}
              <span className="ml-2 font-label text-[11px] tracking-widest text-background-300">
                {String(index + 1).padStart(2, "0")} / {String(items.length).padStart(2, "0")}
              </span>
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={goPrev}
                aria-label="上一个焦点"
                className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border border-background-50/30 text-background-50 transition-colors hover:bg-background-50/10"
              >
                <i className="ri-arrow-left-s-line text-lg" />
              </button>
              <button
                type="button"
                onClick={goNext}
                aria-label="下一个焦点"
                className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border border-background-50/30 text-background-50 transition-colors hover:bg-background-50/10"
              >
                <i className="ri-arrow-right-s-line text-lg" />
              </button>
            </div>
          </div>
        </div>

        <div className="absolute right-6 top-8 hidden w-[228px] flex-col gap-2 xl:flex">
          {items.map((item, itemIndex) => (
            <button
              key={item.id}
              type="button"
              onClick={() => setIndex(itemIndex)}
              className={`flex cursor-pointer items-center gap-3 rounded-lg border p-2 text-left transition-colors ${
                itemIndex === index
                  ? "border-accent-400/70 bg-primary-950/55 backdrop-blur-sm"
                  : "border-background-50/15 bg-primary-950/25 hover:border-background-50/35"
              }`}
            >
              <span className="h-14 w-10 shrink-0 overflow-hidden rounded-md">
                <img src={item.cover} alt={item.title} className="h-full w-full object-cover object-top" />
              </span>
              <span className="min-w-0">
                <span className="block truncate text-[13px] font-medium text-background-50">{item.title}</span>
                <span className="mt-0.5 block text-[11px] text-background-300">
                  {typeLabelMap[item.type]} · {item.rating.toFixed(1)}
                </span>
              </span>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}